import React, { useMemo, useState } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Check, Edit, MoreVertical, Trash2, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PrimaryLink,
  Text,
} from '@/_components';
import { ActionButton } from '@/_containers/actions';
import { RichText } from '@/_containers/inputs/rich-text';
import { useAuthContext } from '@/_providers/auth-provider';
import { dateTime, nameResolver, noop } from '@/_shared';

import { TEXT_EDITOR_HEIGHT, TEXT_EDITOR_HEIGHT_FOCUSED } from './settings';

import { useUpdateNoteMutation } from '../apis';
import type { NoteContextTypeSchema, NoteQuerySchema } from '../schemas/notes-query-schema';

dayjs.extend(relativeTime);

type NotesItemProps = {
  note: NoteQuerySchema;
  onDelete: (noteId: number) => void;
  contextType: NoteContextTypeSchema;
  contextId: string;
  isDeleting?: boolean;
  maxMessageLength?: number;
  key?: React.Key;
};

export const NotesItem = React.memo(function NotesItem({
  note,
  onDelete,
  contextType,
  contextId,
  isDeleting,
}: NotesItemProps) {
  const [editMessage, setEditMessage] = useState('');
  const { user } = useAuthContext();

  const updateNoteMutation = useUpdateNoteMutation(contextType, contextId);

  const handleDelete = () => {
    if (onDelete && note.id) {
      onDelete(note.id);
    }
  };

  const handleEdit = () => {
    setEditMessage(note.message);
  };

  const handleSaveEdit = async () => {
    if (!editMessage.trim() || !note.id) return;

    await updateNoteMutation.mutateAsync({
      id: note.id,
      message: editMessage.trim(),
    });

    setEditMessage('');
  };

  const handleCancelEdit = () => {
    setEditMessage('');
  };

  const authorName = nameResolver(note.user.first_name, note.user.last_name);

  const AuthorNameComponent = useMemo(() => {
    if (note.user.team_member_id) {
      return (
        <PrimaryLink
          to={`/team-members/$id`}
          params={{ id: `${note.user.team_member_id}` }}
          state={{ naturalBack: true }}
        >
          <Text className="text-primary font-medium">{authorName}</Text>
        </PrimaryLink>
      );
    }

    return <Text className="text-primary font-medium">{authorName}</Text>;
  }, [authorName, note.user.team_member_id]);

  const isNoteOwner = note.user_id === user?.id;

  const timestamp = dayjs(dateTime.format(note.date, 'YYYY-MM-DD HH:mm:ss', true)).fromNow();
  const displayMessage = note.message;
  const profilePicture = note.user.team_member?.profile_image?.[0]?.url ?? note.user.profile_images?.[0]?.url;

  return (
    <article className={twMerge('rounded-3xl px-4 py-3', isNoteOwner && 'dark:bg-gray-850 bg-gray-100')}>
      <div className="mb-4 flex items-start gap-3">
        <Avatar>
          <AvatarImage src={profilePicture} alt={authorName} />
          <AvatarFallback name={authorName} className="text-sm">
            {authorName.charAt(0)}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col gap-0.5">
          {AuthorNameComponent}

          <div className="text-c-card-muted flex items-center gap-1 text-xs italic">
            {timestamp}
            {note.edited && (
              <>
                <span className="text-c-card-muted text-xs">•</span>
                <span className="text-c-card-muted text-xs italic">Edited</span>
              </>
            )}
          </div>
        </div>

        {/* Options menu */}
        {isNoteOwner && (
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton title="Note Options" size="xs" disabled={isDeleting} icon={MoreVertical}></ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit className="size-4" />
                  <span>Edit</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDelete}
                  className="text-destructive focus:text-destructive"
                  disabled={isDeleting}
                >
                  <Trash2 className="size-4" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Message content */}
      {editMessage ? (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <RichText
              label=""
              value={editMessage}
              onChange={setEditMessage}
              placeholder="Edit your note..."
              inline={false}
              messengerMode
              disabled={updateNoteMutation.isPending}
              autoFocus
              autoResize
              min_height={TEXT_EDITOR_HEIGHT}
              max_height={TEXT_EDITOR_HEIGHT_FOCUSED * 4}
            />
          </div>

          {/* Inline edit action buttons */}
          <div className="flex h-12 gap-2 py-1">
            <Button
              onClick={handleCancelEdit}
              as="outline"
              className="h-10 w-10 shrink-0 rounded-full p-0"
              size="sm"
              disabled={updateNoteMutation.isPending}
            >
              <X className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateNoteMutation.isPending || !editMessage.trim()}
              className="h-10 w-10 shrink-0 rounded-full p-0 pr-0.5"
              iconClassName="size-5"
              size="sm"
              showLoader={updateNoteMutation.isPending}
              icon={Check}
            />
          </div>
        </div>
      ) : (
        <div className="text-accent block w-full text-sm dark:text-white">
          <RichText inline value={displayMessage} onChange={noop} disabled />
        </div>
      )}
    </article>
  );
});
